import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Tag } from "lucide-react";

const ARTICLES = [
  {
    slug: "ameliorer-referencement-seo",
    title: "Comment ameliorer son referencement naturel en 2026",
    excerpt: "Decouvrez les strategies SEO les plus efficaces pour ameliorer la visibilite de votre site web sur Google. Mots-cles, contenu, technique et backlinks.",
    category: "SEO",
    date: "2026-03-01",
    readTime: "8 min",
    content: `Le referencement naturel (SEO) reste le levier le plus puissant pour generer du trafic qualifie vers votre site web. Voici les strategies cles pour 2026.\n\n## 1. Optimisez votre contenu\nCreez du contenu de qualite qui repond aux intentions de recherche de vos utilisateurs. Utilisez des mots-cles pertinents naturellement dans vos textes.\n\n## 2. Ameliorez la technique\nVitesse de chargement, compatibilite mobile, donnees structurees : les facteurs techniques sont essentiels pour un bon positionnement.\n\n## 3. Construisez votre autorite\nLes backlinks de qualite restent un signal fort pour Google. Creez du contenu qui merite d'etre cite.\n\n## 4. Utilisez les outils d'analyse\nGoogle Analytics et Search Console vous donnent les donnees necessaires pour piloter votre strategie SEO. GLOTECH OS integre ces outils directement dans votre dashboard.`,
  },
  {
    slug: "gerer-reseaux-sociaux-entreprise",
    title: "Comment gerer ses reseaux sociaux efficacement en entreprise",
    excerpt: "Strategies et outils pour une presence sociale efficace. Planification, contenu, engagement et automatisation.",
    category: "Marketing",
    date: "2026-02-20",
    readTime: "6 min",
    content: `La gestion des reseaux sociaux est devenue incontournable pour toute entreprise souhaitant developper sa visibilite.\n\n## 1. Definissez votre strategie\nChoisissez les plateformes adaptees a votre cible : Facebook, Instagram, LinkedIn ou TikTok.\n\n## 2. Planifiez votre contenu\nUtilisez un calendrier editorial pour planifier vos publications a l'avance. La regularite est la cle.\n\n## 3. Automatisez la publication\nDes outils comme GLOTECH OS permettent de programmer vos publications sur toutes les plateformes depuis une interface unique.\n\n## 4. Analysez vos resultats\nSuivez vos statistiques d'engagement pour optimiser votre strategie.`,
  },
  {
    slug: "surveiller-concurrents-seo",
    title: "Comment surveiller ses concurrents en SEO",
    excerpt: "Apprenez a analyser les positions de vos concurrents et a identifier les opportunites pour ameliorer votre propre referencement.",
    category: "SEO",
    date: "2026-02-10",
    readTime: "7 min",
    content: `La veille concurrentielle SEO est essentielle pour rester competitif sur les moteurs de recherche.\n\n## 1. Identifiez vos concurrents\nAnalysez qui se positionne sur vos mots-cles strategiques.\n\n## 2. Comparez les positions\nSuivez l'evolution des positions de vos concurrents sur les mots-cles importants.\n\n## 3. Analysez leur contenu\nEtudiez le type de contenu qui performe chez vos concurrents.\n\n## 4. Utilisez GLOTECH OS\nLe module de surveillance concurrents vous permet de suivre automatiquement les positions de vos rivaux.`,
  },
  {
    slug: "automatiser-marketing-digital",
    title: "Comment automatiser son marketing digital",
    excerpt: "Gagnez du temps et ameliorez vos resultats grace a l'automatisation de vos campagnes marketing. Email, reseaux sociaux, relances.",
    category: "Marketing",
    date: "2026-01-28",
    readTime: "5 min",
    content: `L'automatisation marketing permet de gagner un temps precieux tout en ameliorant l'efficacite de vos campagnes.\n\n## 1. Automatisez vos emails\nRelances de paiement, confirmations et notifications peuvent etre entierement automatisees.\n\n## 2. Programmez vos publications\nUtilisez un outil comme GLOTECH OS pour programmer vos posts sur tous vos reseaux sociaux.\n\n## 3. Generez du contenu avec l'IA\nL'assistant IA integre peut generer des articles de blog, des posts sociaux et des series de contenu.\n\n## 4. Suivez les resultats\nLes dashboards KPI vous permettent de mesurer l'impact de vos automatisations.`,
  },
];

export function BlogListPage() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-12">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">Blog <span className="text-[#0099CC]">GLOTECH</span></h1>
        <p className="text-lg text-[#94A3B8]">Conseils et strategies pour developper votre activite digitale.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-2 gap-8">
          {ARTICLES.map(a => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="group bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden hover:border-[#0099CC]/30 transition-all" data-testid={`blog-card-${a.slug}`}>
              <div className="h-48 bg-gradient-to-br from-[#0099CC]/10 to-[#0077AA]/5 flex items-center justify-center">
                <Tag className="h-12 w-12 text-[#0099CC]/30" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs bg-[#0099CC]/10 text-[#0099CC] px-2.5 py-1 rounded-full font-medium">{a.category}</span>
                  <span className="text-xs text-[#64748b] flex items-center gap-1"><Clock className="h-3 w-3" />{a.readTime}</span>
                </div>
                <h2 className="text-white font-bold text-lg mb-2 group-hover:text-[#0099CC] transition-colors">{a.title}</h2>
                <p className="text-[#64748b] text-sm leading-relaxed mb-4">{a.excerpt}</p>
                <div className="flex items-center gap-1 text-[#0099CC] text-sm font-medium">
                  Lire l'article <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function BlogArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h1 className="text-3xl font-bold mb-4">Article introuvable</h1>
        <Link to="/blog" className="text-[#0099CC] hover:underline">Retour au blog</Link>
      </div>
    );
  }

  return (
    <div>
      <article className="max-w-3xl mx-auto px-6 py-20">
        <Link to="/blog" className="text-[#0099CC] text-sm hover:underline mb-6 inline-block">&larr; Retour au blog</Link>
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs bg-[#0099CC]/10 text-[#0099CC] px-2.5 py-1 rounded-full font-medium">{article.category}</span>
          <span className="text-xs text-[#64748b] flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(article.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="text-xs text-[#64748b] flex items-center gap-1"><Clock className="h-3 w-3" />{article.readTime}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-8">{article.title}</h1>
        <div className="prose prose-invert prose-sm max-w-none text-[#94A3B8] leading-relaxed space-y-4">
          {article.content.split("\n\n").map((para, i) => {
            if (para.startsWith("## ")) return <h2 key={i} className="text-white text-xl font-bold mt-8 mb-3">{para.replace("## ", "")}</h2>;
            return <p key={i}>{para}</p>;
          })}
        </div>
        <div className="mt-16 bg-[#151B2B] border border-[#2A3441] rounded-xl p-8 text-center">
          <h3 className="text-white font-bold text-xl mb-3">Decouvrez GLOTECH OS</h3>
          <p className="text-[#64748b] text-sm mb-6">La plateforme tout-en-un pour gerer et developper votre activite.</p>
          <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] text-white px-6 py-3 rounded-xl font-semibold">
            Demander une demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
